import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  CircularProgress,  
  Table, 
  TableBody,
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../components/hooks/useTablePaginationInit';
import useTableSortInit from '../../../../components/hooks/useTableSortInit';
import TableTimeStamps from '../../../../components/utils/TableTimeStamps';
import TopBar from '../../../../components/utils/TopBar';


import { 
  GetUserConsentsBIZidentity 
} from '../../../../redux/actions/business/identity/userConsentsActions';
import NA from '../../../../components/utils/NA';
import Copyable from '../../../../components/utils/Copyable';

const UserConsentsPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.identity"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [userConsentsList, setUserConsentsList] = useState([]);
  const [userConsentsTotal, setUserConsentsTotal] = useState(0);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const [
    sort,
    sortDirection,
    handleSort
  ] = useTableSortInit();

  // searching from a later page would land on an empty result set
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    handleChangePage(null, 0);
  };

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "User",
      sort: "ref_user_id",
    },
    {
      label: "Terms Version",
      sort: "terms_version",
    }, 
    {
      label: "Privacy Version",
      sort: "privacy_version",
    },
    {
      label: "Source",
      sort: "source",
    },
    {
      label: "IP address",
      sort: "ip_address",
    },
    {
      label: "Time stamps",
      sort: "createdAt",
    }
  ];


  useEffect(() => {
   handleGetUserConsents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      formatUsersList(state.userConsentsList.records || []);
      setUserConsentsTotal(state.userConsentsList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatUsersList = (list) => {
    setUserConsentsList(list);
  };

  async function handleGetUserConsents(customLoading){
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&field=${sort}`;
    if(sortDirection) query += `&order=${sortDirection}`;
    await dispatch(GetUserConsentsBIZidentity(page+1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  }

  return (
    <Box>

      <TopBar
        title={"User Consents"}
        search={search}
        searchPlaceholder={"Search id, user, version, source or IP"}
        onSearchChange={handleSearchChange}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          userConsentsList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No user consent found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                userConsentsList.map((row, index) => (
                  <TableRow 
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        className="flexCenter_Row"
                        gap={"10px"}>
                        <Typography
                           variant='body2'>
                          {row?.user?.primary_email || <NA />}
                        </Typography>
                      </Box>
                      <Typography
                        color={"grey"}
                        variant='sm_caption'>
                        <Copyable>
                          {row?.user?.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.terms_version || <NA />}
                    </TableCell>
                    <TableCell>
                      {row.privacy_version || <NA />}
                    </TableCell>
                    <TableCell>
                      {row.source || <NA />}
                    </TableCell>
                    <TableCell>
                      <Copyable>
                        {row.ip_address || <NA />}
                      </Copyable>
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.createdAt}
                        updatedAt={row.updatedAt}
                        />
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={userConsentsTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
    </Box>
  )
}

export default UserConsentsPage;